import React, { useState } from 'react';
import { IoTrash } from 'react-icons/io5';
import ATable, { ATableHeader } from '../../../organisms/ATable';
import NewTooltip from '../../../atoms/NewTooltip';
import { formatCurrency } from '../structuringUtils';
import { Document, expandDocumentsToRows, buildTableHeaders } from '../OperationPageViewBackend';
import { editedReceivablesStore } from '../EditedReceivablesStore';
import { toast } from 'react-toastify';

interface OperationTableViewProps {
    documents: Document[];
    isDeleting: boolean;
    isOperationClosed: boolean;
    onDeleteInstallment: (documentId: string, installmentNumber: string) => void;
    onReceivableChange?: (document: Document, receivable: import('../OperationPageViewBackend').Receivable) => void;
}

const OperationTableView: React.FC<OperationTableViewProps> = ({
    documents,
    isDeleting,
    isOperationClosed,
    onDeleteInstallment,
    onReceivableChange,
}) => {
    const [editValues, setEditValues] = useState<Record<string, { due_date: string; amount: string }>>({});
    const [focusedAmount, setFocusedAmount] = useState<string | null>(null);

    const expandedData = expandDocumentsToRows(documents).sort((a, b) =>
        (a.document_title || '').localeCompare(b.document_title || '', 'pt-BR', { numeric: true })
    );
    const tableHeaders: ATableHeader[] = buildTableHeaders();

    const getRowStableKey = (rowIndex: number) => {
        const row = expandedData[rowIndex];
        return row ? `${row.document_id}-${row.receivable_number}` : `${rowIndex}`;
    };

    const handleValueChange = (rowIndex: number, field: 'due_date' | 'amount', value: string) => {
        const key = `${getRowStableKey(rowIndex)}-${field}`;
        setEditValues(prev => ({
            ...prev,
            [key]: { ...prev[key], [field]: value }
        }));
    };

    const getCurrentReceivable = (row: any) => {
        const stored = editedReceivablesStore.getAll().find(
            e => e.document_id === row.document_id && e.receivable_number === row.receivable_number
        );
        return stored?.receivable || row.receivable_raw;
    };

    const handleAmountBlur = (rowIndex: number, row: any) => {
        const raw = String(getCellValue(rowIndex, 'amount', row.amount)).replace(',', '.');
        const numeric = parseFloat(raw);
        const key = `${getRowStableKey(rowIndex)}-amount`;

        if (isNaN(numeric) || numeric < 0 || !/^\d+(\.\d{1,2})?$/.test(raw.trim())) {
            toast.error('Valor inválido. Informe um valor monetário válido (ex: 1234,56)');
            return;
        }

        setFocusedAmount(null);
        setEditValues(prev => ({ ...prev, [key]: { ...prev[key], amount: String(numeric) } }));

        if (onReceivableChange && row.document_raw && row.receivable_raw) {
            onReceivableChange(row.document_raw, { ...getCurrentReceivable(row), amount: numeric });
        }
    };

    const handleDueDateChange = (rowIndex: number, value: string, row: any) => {
        handleValueChange(rowIndex, 'due_date', value);
        if (onReceivableChange && row.document_raw && row.receivable_raw) {
            onReceivableChange(row.document_raw, { ...getCurrentReceivable(row), due_date: value });
        }
    };

    const getCellValue = (rowIndex: number, field: 'due_date' | 'amount', originalValue: any) => {
        const key = `${getRowStableKey(rowIndex)}-${field}`;
        return editValues[key]?.[field] ?? originalValue;
    };

    if (expandedData.length === 0) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">Nenhum recebível encontrado.</p>
            </div>
        );
    }

    return (
        <div className="overflow-x-auto">
            <div className="flex justify-between items-center mb-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                    {expandedData.length} recebíveis
                </div>
            </div>

            <ATable headers={tableHeaders} autoResize={true} marginTop={350}>
                {expandedData.map((row, index) => (
                    <tr key={row.document_id ? `${row.document_id}-${row.receivable_number}` : `missing-doc-${index}-${row.receivable_number}`} className={`${index % 2 === 0 ? 'bg-slate-100 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-300 dark:hover:bg-slate-600 hover:shadow-md border-b border-gray-300 dark:border-gray-600`}>
                        <td className="py-2 px-2 text-xs dark:text-gray-300 whitespace-nowrap">{row.document_id || '-'}</td>
                        <td className="py-2 px-2 text-xs dark:text-gray-300 whitespace-nowrap">{row.document_title}</td>
                        <td className="py-2 px-2 text-xs text-center dark:text-gray-300 whitespace-nowrap">{row.receivable_number}</td>
                        <td className="py-2 px-2 text-xs text-center dark:text-gray-300">
                            <input
                                type="text"
                                value={getCellValue(index, 'due_date', row.due_date)}
                                onChange={(e) => handleDueDateChange(index, e.target.value, row)}
                                disabled={isOperationClosed}
                                placeholder="DD/MM/AAAA"
                                className="min-w-[120px] px-2 py-1 text-xs text-center border border-gray-300 rounded bg-white dark:bg-slate-700 dark:text-gray-300 dark:border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                        </td>
                        <td className="py-2 px-2 text-xs text-center">
                            <input
                                type="text"
                                value={focusedAmount === `${index}` ? String(getCellValue(index, 'amount', row.amount)) : formatCurrency(Number(String(getCellValue(index, 'amount', row.amount)).replace(',', '.')))}
                                onFocus={() => setFocusedAmount(`${index}`)}
                                onBlur={() => handleAmountBlur(index, row)}
                                onChange={(e) => handleValueChange(index, 'amount', e.target.value.replace(/[^\d,.]/g, ''))}
                                disabled={isOperationClosed}
                                placeholder="0,00"
                                className="min-w-[130px] px-2 py-1 text-xs text-center border border-gray-300 rounded bg-white dark:bg-slate-700 text-green-600 dark:text-green-400 dark:border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                        </td>
                        <td className="py-2 px-2 text-xs dark:text-gray-300 whitespace-nowrap">{row.assignor_name}</td>
                        <td className="py-2 px-2 text-xs dark:text-gray-300 whitespace-nowrap">{row.debtor_name}</td>
                        <td className="py-2 px-2 text-xs text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                row.processing_status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                row.processing_status === 'error' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                                'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                            }`}>
                                {row.processing_status === 'completed' ? 'Concluído' :
                                 row.processing_status === 'error' ? 'Erro' :
                                 row.processing_status === 'pending' ? 'Pendente' :
                                 row.processing_status || '-'}
                            </span>
                        </td>
                        <td className="py-2 px-2 text-xs text-red-600 dark:text-red-400">{row.processing_error || '-'}</td>
                        <td className="py-2 px-2 text-xs text-center">
                            <div className="flex justify-center space-x-2">
                                <NewTooltip content="Deletar parcela" alignment="left">
                                    <button
                                        onClick={() => onDeleteInstallment(row.document_id, row.receivable_number)}
                                        disabled={isDeleting || isOperationClosed}
                                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <IoTrash className="w-4 h-4" />
                                    </button>
                                </NewTooltip>
                            </div>
                        </td>
                    </tr>
                ))}
            </ATable>
        </div>
    );
};

export default OperationTableView;
